import React, { forwardRef, useState } from 'react';
import { clsx } from 'clsx';
import styles from './Input.module.scss';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input label text
   */
  label?: string;

  /**
   * Helper text displayed below the input
   */
  helperText?: string;

  /**
   * Error message text
   */
  errorText?: string;

  /**
   * Whether the input has an error state
   */
  error?: boolean;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Input size variant
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Input variant style
   */
  variant?: 'default' | 'filled';

  /**
   * Icon to display on the right side of the input
   */
  rightIcon?: React.ReactNode;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Input container class name
   */
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      errorText,
      error = false,
      disabled = false,
      size = 'medium',
      variant = 'default',
      rightIcon,
      className,
      containerClassName,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [hasValue, setHasValue] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setHasValue(e.target.value.length > 0);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(e.target.value.length > 0);
      props.onChange?.(e);
    };

    const isError = error || !!errorText;
    const isFilled = variant === 'filled' && hasValue;
    const showFloatingLabel = label && hasValue;

    const inputClasses = clsx(
      styles.input,
      styles[`size--${size}`],
      {
        [styles.focused]: isFocused,
        [styles.filled]: isFilled,
        [styles.error]: isError,
        [styles.disabled]: disabled,
        [styles.hasRightIcon]: !!rightIcon,
      },
      className
    );

    const containerClasses = clsx(
      styles.container,
      {
        [styles.focused]: isFocused,
        [styles.filled]: isFilled,
        [styles.error]: isError,
        [styles.disabled]: disabled,
      },
      containerClassName
    );

    const labelClasses = clsx(styles.label, {
      [styles.labelFloating]: showFloatingLabel,
      [styles.labelError]: isError,
      [styles.labelHidden]: !showFloatingLabel,
    });

    const fieldClasses = clsx(styles.field, {
      [styles.error]: isError,
      [styles.focused]: isFocused,
      [styles.filled]: isFilled,
      [styles.disabled]: disabled,
    });

    return (
      <div className={containerClasses}>
        <div className={fieldClasses}>
          <div className={styles.inputWrapper}>
            {label && (
              <label className={labelClasses} htmlFor={props.id}>
                {label}
              </label>
            )}

            <input
              ref={ref}
              className={inputClasses}
              disabled={disabled}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder={showFloatingLabel ? '' : props.placeholder}
              {...props}
            />
          </div>

          {rightIcon && (
            <div className={styles.rightIcon} aria-hidden="true">
              {rightIcon}
            </div>
          )}
        </div>

        {(errorText || helperText) && (
          <div className={styles.helper}>
            <span
              className={clsx(styles.helperText, {
                [styles.errorText]: isError,
              })}
            >
              {errorText || helperText}
            </span>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
